// composables
import { ref, toRaw, type Ref } from 'vue'
// utilities
import dayjs from 'dayjs'
import { useI18n } from 'vue-i18n'
// stores
import { useAuthStore } from '@/stores/AuthStore'
// composables
import usePhoneValidator from '@/composables/usePhoneValidator'
// types
import type { Agreement, DealershipCommsConfigItem } from '@/types/domain/agreement'

/**
 * Composable for managing template placeholders
 * Handles placeholder initialization and replacement for email/SMS templates
 */
export default function useAgreementPlaceholders(
  agreementData: Ref<Agreement | null>,
  selectedCallCenterCommsConfig: Ref<DealershipCommsConfigItem | null>
) {
  const { n } = useI18n()
  const authStore = useAuthStore()

  // state
  const placeholders = ref<Array<{ placeholder: string; value: string | number | null }>>([])

  // phone validator for dealership phone number
  const fromNumberValidator = usePhoneValidator()

  /**
   * Initializes placeholders array with all available placeholders
   */
  function initPlaceholders(): void {
    if (!agreementData.value || !selectedCallCenterCommsConfig.value) {
      return
    }

    // Validate phone number if available
    if (selectedCallCenterCommsConfig.value.phoneNumber) {
      fromNumberValidator.validatePhoneNumber('+' + selectedCallCenterCommsConfig.value.phoneNumber)
    }

    placeholders.value = [
      {
        placeholder: '{{email_greeting}}',
        value: selectedCallCenterCommsConfig.value.emailConfig?.greeting || ''
      },
      {
        placeholder: '{{email_sign_off}}',
        value: selectedCallCenterCommsConfig.value.emailConfig?.signOff || ''
      },
      {
        placeholder: '{{on_behalf_of}}',
        value: selectedCallCenterCommsConfig.value.emailConfig?.onBehalfOf || ''
      },
      {
        placeholder: '{{sms_greeting}}',
        value: 'Hi {{customer_name}},'
      },
      {
        placeholder: '{{sms_sign_off}}',
        value: '{{user_forename}} - {{dealership_name}}'
      },
      {
        placeholder: '{{customer_name}}',
        value: agreementData.value.customer.name ?? 'customer'
      },
      {
        placeholder: '{{customer_forename}}',
        value: agreementData.value.customer.isCompany
          ? agreementData.value.customer.companyName
          : agreementData.value.customer.forename
      },
      {
        placeholder: '{{customer_surname}}',
        value: agreementData.value.customer.isCompany
          ? agreementData.value.customer.companyName
          : agreementData.value.customer.surname
      },
      {
        placeholder: '{{agreement_id}}',
        value: agreementData.value.id
      },
      {
        placeholder: '{{agreement_end_date}}',
        value: dayjs(agreementData.value.endDate).format('DD/MM/YYYY')
      },
      {
        placeholder: '{{agreement_final_repayment}}',
        value: agreementData.value.finalRepayment ?? ''
      },
      {
        placeholder: '{{agreement_product_type}}',
        value: agreementData.value.productType
      },
      {
        placeholder: '{{manufacturer_brand_color}}',
        value: agreementData.value.dealership.manufacturerBrandColor
      },
      {
        placeholder: '{{agreement_months_remaining}}',
        value: agreementData.value.periodRemaining ?? ''
      },
      {
        placeholder: '{{agreement_product}}',
        value: agreementData.value.productType
      },
      {
        placeholder: '{{vehicle}}',
        value: agreementData.value.vehicle
          ? `${agreementData.value.vehicle.make} ${agreementData.value.vehicle.model}`
          : 'vehicle'
      },
      {
        placeholder: '{{vehicle_image_url}}',
        value: agreementData.value.vehicle.imageUrl
      },
      {
        placeholder: '{{vehicle_category}}',
        value: agreementData.value.vehicle.category
      },
      {
        placeholder: '{{vehicle_vrm}}',
        value: agreementData.value.vehicle.vrm
      },
      {
        placeholder: '{{vehicle_make}}',
        value: agreementData.value.vehicle.make ?? ''
      },
      {
        placeholder: '{{vehicle_model}}',
        value: agreementData.value.vehicle.model ?? ''
      },
      {
        placeholder: '{{odometer_allowance_yearly}}',
        value: agreementData.value.odometer.allowanceYearly
          ? n(agreementData.value.odometer.allowanceYearly, 'default')
          : '-'
      },
      {
        placeholder: '{{odometer_unit}}',
        value: agreementData.value.odometer.unit ?? ''
      },
      {
        placeholder: '{{odometer_expected}}',
        value: agreementData.value.odometer.expected ? n(agreementData.value.odometer.expected, 'default') : '-'
      },
      {
        placeholder: '{{dealership_name}}',
        value: agreementData.value.dealership.displayName ?? 'the dealership'
      },
      {
        placeholder: '{{dealership_assigned_phone_number}}',
        value: fromNumberValidator.validatedData.value
          ? toRaw(fromNumberValidator.validatedData.value)?.number?.national || ''
          : ''
      },
      {
        placeholder: '{{dealership_assigned_email}}',
        value: selectedCallCenterCommsConfig.value.emailAddress || ''
      },
      {
        placeholder: '{{dealership_assigned_sms_number}}',
        value: selectedCallCenterCommsConfig.value.smsNumber || ''
      },
      {
        placeholder: '{{user_name}}',
        value: authStore.details.name ?? 'Engagement Team'
      },
      {
        placeholder: '{{user_forename}}',
        value: authStore.details.forename ?? 'Engagement Team'
      },
      {
        placeholder: '{{user_surname}}',
        value: authStore.details.surname ?? 'Engagement Team'
      }
    ]
  }

  /**
   * Replaces placeholders in a string with their values
   * @param value - The string containing placeholders
   * @returns The string with placeholders replaced
   */
  function replacePlaceholders(value: string): string {
    let newValue = value
    let placeholdersFound = false

    placeholders.value.forEach(placeholder => {
      if (newValue?.includes(placeholder.placeholder)) {
        newValue = newValue.replace(placeholder.placeholder, String(placeholder.value))
        placeholdersFound = true
      }
    })

    // If any placeholders were found and replaced, check for nested placeholders
    if (placeholdersFound) {
      newValue = replacePlaceholders(newValue)
    }

    return newValue
  }

  /**
   * Converts HTML br tags to newlines
   * @param str - The string containing br tags
   * @returns The string with br tags converted to newlines
   */
  function brToNewLine(str: string): string {
    return str?.replace(/<br\s*\/?>/gm, '\n')
  }

  return {
    // state
    placeholders,
    // methods
    initPlaceholders,
    replacePlaceholders,
    brToNewLine
  }
}
